import {organizationApiClient} from 'organization/organizationApiClient'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {StripeConnection} from 'stripe/stripeConnection'
import {useRouteOrganizationId} from 'utils/lib/url'
import {useLocalizedNavigate} from 'i18n/useLocalizedNavigate'
import {useOrganizationRoutesStatic} from 'organization/OrganizationRoutes'

export interface StripeConnectionContextProps {
  addConnection: () => void
  removeConnection: () => void
  getDashboard: () => void
  stripeConnection: StripeConnection | null
  connected: (stripeAccountId: string) => void
  loading: boolean
}

export const StripeConnectionContext = React.createContext<
  StripeConnectionContextProps | undefined
>(undefined)

export default function StripeConnectionProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [loading, setLoading] = useState<boolean>(true)
  const organizationId = useRouteOrganizationId()
  const [stripeConnection, setStripeConnection] =
    useState<StripeConnection | null>(null)
  const [initLoading, setInitLoading] = useState<boolean>(true)
  const navigate = useLocalizedNavigate()
  const routes = useOrganizationRoutesStatic(organizationId ?? -1)

  useEffect(() => {
    setInitLoading(true)
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async () => {
    await apiClient
      .get(`api/organization/${organizationId}/stripe/connection`)
      .then((response) => {
        setStripeConnection(response)
      })
      .finally(() => {
        setLoading(false)
        setInitLoading(false)
      })
  }

  const addConnection = async () => {
    setLoading(true)

    await apiClient
      .post(`api/organization/${organizationId}/stripe/addConnection`)
      .then((response) => {
        window.open(response.url)?.focus()
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const removeConnection = async () => {
    setLoading(true)

    await apiClient
      .remove(`api/organization/${organizationId}/stripe/disconnectConnection`)
      .then(() => {
        setStripeConnection(null)
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const getDashboard = async () => {
    setLoading(true)

    return await apiClient
      .get(`api/organization/${organizationId}/stripe/dashboard`)
      .then((response) => {
        window.open(response.url, '_blank')?.focus()
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const connected = async (stripeAccountId: string) => {
    setLoading(true)

    await apiClient
      .post(
        `api/organization/${organizationId}/stripe/${stripeAccountId}/connected`,
      )
      .then((response) => {
        get()
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
      })
      .finally(() => {
        setLoading(false)
        console.log('navigating', routes.settings.root)
        navigate(routes.settings.root)
      })
  }

  return (
    <StripeConnectionContext.Provider
      value={{
        addConnection,
        removeConnection,
        getDashboard,
        connected,
        stripeConnection,
        loading,
      }}
    >
      {initLoading && <FullPageLoader />}
      {!initLoading && props.children}
    </StripeConnectionContext.Provider>
  )
}

export function useStripeConnection() {
  const context = React.useContext(StripeConnectionContext)
  if (context === undefined) {
    throw new Error(
      'useStripeConnection must be used within StripeConnectionProvider',
    )
  }
  return context
}
